import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { AbstractApiV2Service } from '../../../api/services/abstract-api-v2.service';
import { OrganizationData } from '../interfaces/OrganizationData';
import { SimpleRouteReuseStrategy } from '../../../route-cache-strategies/simple-route-reuse.strategy';
import { BackendErrorTranslatorService } from '../../../services/localization/backend-error-translator.service';
import { IdentityService } from '../../auth/services/identity.service';

@Injectable({
    providedIn: 'root',
})
export class OrganizationsService extends AbstractApiV2Service<OrganizationData> {
    public temporarySelectedOrganizationSubject: BehaviorSubject<OrganizationData | null>;
    public companiesVisibleForUser: OrganizationData[];
    public isAllManagedCompaniesSelected = false;
    public loadedVisibleCompanies: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected resourceName = 'companies';
    private selectedOrganizationSubject: BehaviorSubject<OrganizationData | null>;

    public constructor(
        public readonly identityService: IdentityService,
        private readonly simpleRouteReuseStrategy: SimpleRouteReuseStrategy,
        http: HttpClient,
        backendErrorTranslator: BackendErrorTranslatorService,
    ) {
        super(http, backendErrorTranslator);

        this.readSelectedOrganizationData();

        // invalidate route caching if the selected organization changes
        this.selectedOrganization$().subscribe(() => {
            simpleRouteReuseStrategy.invalidateRouteCache();
        });
    }

    /**
     * This observable will reflect the changes in the company selector.
     * @param isGlobalSelector
     */
    public selectedOrganization$(isGlobalSelector = true): Observable<OrganizationData | null> {
        if (!isGlobalSelector && !this.temporarySelectedOrganizationSubject) {
            this.temporarySelectedOrganizationSubject = new BehaviorSubject<OrganizationData | null>(null);
        }

        return isGlobalSelector
            ? this.selectedOrganizationSubject.asObservable()
            : this.temporarySelectedOrganizationSubject.asObservable();
    }

    /**
     * Note that this method will return the organization id
     * that is selected _at the moment of the call_.
     * Subscribe to `selectedOrganization$()` if you need to be notified when the selection changes.
     * @param isGlobalSelector
     */
    public currentlySelectedOrganization(isGlobalSelector = true): OrganizationData | null {
        if (!this.temporarySelectedOrganizationSubject) {
            this.temporarySelectedOrganizationSubject = new BehaviorSubject<OrganizationData | null>(null);
        }
        return isGlobalSelector ? this.selectedOrganizationSubject.getValue() : this.temporarySelectedOrganizationSubject.getValue();
    }

    /* Check if current selected organization has children */
    public currentlySelectedOrganizationHasChildren(): boolean | undefined {
        const currentOrg = this.currentlySelectedOrganization();
        if (!currentOrg) {
            return this.identityService.isCompanySelectorVisible();
        }

        return currentOrg.details?.configuration?.features?.enableCompanySelector === true;
    }

    /**
     * Check if the newly logged user already selected an organization, otherwise select 'All managed companies' option
     * @param SelectedOrganization
     */
    public initializeSelectedOrganization(SelectedOrganization: OrganizationData | null): void {
        if (!localStorage.getItem('selectedOrganization')) {
            /* the user logged in for the first time or after a logout (where local storage has been cleared) */
            this.selectAllOrganizations();
        }
    }

    /**
     * Select and set organization from selector.
     * If isGlobalSelector is true, the selector will update inside all pages
     * @param organization
     * @param isGlobalSelector
     */
    public selectOrganization(organization: OrganizationData | null, isGlobalSelector = true) {
        if (isGlobalSelector) {
            this.isAllManagedCompaniesSelected = false;
            this.selectedOrganizationSubject.next(organization);
            localStorage.setItem('selectedOrganization', JSON.stringify(organization));
        } else {
            if (!this.temporarySelectedOrganizationSubject) {
                this.temporarySelectedOrganizationSubject = new BehaviorSubject<OrganizationData | null>(null);
            }
            this.isAllManagedCompaniesSelected = false;
            this.temporarySelectedOrganizationSubject.next(organization);
            this.readSelectedOrganizationData(isGlobalSelector);
        }
    }

    /* Set 'All managed companies' */
    public selectAllOrganizations() {
        this.isAllManagedCompaniesSelected = true;
        this.selectedOrganizationSubject.next(null);
        localStorage.removeItem('selectedOrganization');
    }

    private readSelectedOrganizationData(isGlobalSelector = true) {
        if (!isGlobalSelector) {
            return;
        }

        const savedOrgData = localStorage.getItem('selectedOrganization');
        let selectedOrg: OrganizationData | null = null;

        try {
            selectedOrg = !savedOrgData ? null : JSON.parse(savedOrgData);
        } catch (e) {
            console.error('Cannot parse selected organization data');
        }

        this.selectedOrganizationSubject = new BehaviorSubject<OrganizationData | null>(selectedOrg);
    }
}
